import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Image as ImageIcon, Upload, Loader2, Check, Palette, LayoutTemplate, History, Sparkles } from 'lucide-react';
import DesignerPanel from './DesignerPanel';

const Grid = ({ items, selected, onSelect, base, empty, testPrefix }) => (
  items.length === 0
    ? <p className="text-sm text-muted-foreground py-6 text-center">{empty}</p>
    : (
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-72 overflow-y-auto p-0.5">
        {items.map((it, i) => (
          <button key={it.url} type="button" onClick={() => onSelect(it.url)} className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-colors ${selected === it.url ? 'border-primary' : 'border-transparent hover:border-muted-foreground/40'}`} data-testid={`${testPrefix}-${i}`} title={it.title || it.snippet || ''}>
            <img src={`${base}${it.url}`} alt="" className="w-full h-full object-cover" />
            {selected === it.url && <span className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-0.5"><Check className="w-3 h-3" /></span>}
            {(it.title || it.category) && <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[10px] px-1 py-0.5 truncate">{it.title}</span>}
          </button>
        ))}
      </div>
    )
);

const PhotoPickerDialog = ({ open, onOpenChange, post, onSaved }) => {
  const { API, getAuthHeaders } = useAuth();
  const base = API.replace('/api', '');
  const [library, setLibrary] = useState({ templates: [], past_posts: [], designs: [] });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showDesigner, setShowDesigner] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ai-social/media-library`, getAuthHeaders());
      setLibrary(res.data);
    } catch (err) {
      toast.error('Errore nel caricamento della libreria');
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => {
    if (open) { fetchLibrary(); setSelected(post?.media_urls?.[0] || null); setShowDesigner(false); }
  }, [open, post, fetchLibrary]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/media`, fd, { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } });
      setSelected(res.data.url);
      toast.success('Foto caricata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const save = async (url = selected) => {
    if (!url) { toast.error('Seleziona una foto'); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/ai-social/posts/${post.id}/media`, { media_urls: [url] }, getAuthHeaders());
      toast.success('Foto aggiunta al post');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const headlineGuess = (post?.content || '').split('\n')[0].replace(/[#*_]/g, '').replace(/[.!?…].*$/, '').split(/\s+/).slice(0, 6).join(' ').slice(0, 48);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="photo-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2"><ImageIcon className="w-5 h-5 text-primary" /> Aggiungi una foto al post</DialogTitle>
          <DialogDescription className="line-clamp-2">{post?.content}</DialogDescription>
        </DialogHeader>

        {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> : (
          <Tabs defaultValue={library.templates.length ? 'templates' : 'past'} className="mt-2">
            <TabsList className="w-full flex justify-start gap-1 h-auto p-1">
              <TabsTrigger value="templates" className="text-xs py-1.5 gap-1" data-testid="picker-tab-templates"><LayoutTemplate className="w-3.5 h-3.5 hidden sm:block" /> Creator Studio</TabsTrigger>
              <TabsTrigger value="past" className="text-xs py-1.5 gap-1" data-testid="picker-tab-past"><History className="w-3.5 h-3.5 hidden sm:block" /> Post passati</TabsTrigger>
              <TabsTrigger value="designs" className="text-xs py-1.5 gap-1" data-testid="picker-tab-designs"><Sparkles className="w-3.5 h-3.5 hidden sm:block" /> Grafiche AI</TabsTrigger>
              <TabsTrigger value="upload" className="text-xs py-1.5 gap-1" data-testid="picker-tab-upload"><Upload className="w-3.5 h-3.5 hidden sm:block" /> Carica</TabsTrigger>
            </TabsList>
            <TabsContent value="templates" className="mt-3">
              <Grid items={library.templates} selected={selected} onSelect={setSelected} base={base} empty="Nessuna immagine nei template del Creator Studio." testPrefix="picker-template" />
            </TabsContent>
            <TabsContent value="past" className="mt-3">
              <Grid items={library.past_posts} selected={selected} onSelect={setSelected} base={base} empty="Nessuna foto nei tuoi post precedenti." testPrefix="picker-past" />
            </TabsContent>
            <TabsContent value="designs" className="mt-3">
              <Grid items={library.designs} selected={selected} onSelect={setSelected} base={base} empty="Nessuna grafica generata finora." testPrefix="picker-design" />
            </TabsContent>
            <TabsContent value="upload" className="mt-3">
              <label className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed py-10 cursor-pointer hover:bg-muted/40 text-muted-foreground">
                {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
                <span className="text-sm">Carica una foto dal dispositivo</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="picker-upload-input" />
              </label>
            </TabsContent>
          </Tabs>
        )}

        {selected && (
          <div className="flex items-center gap-3 rounded-lg border p-2 mt-2" data-testid="picker-selected-bar">
            <img src={`${base}${selected}`} alt="" className="w-14 h-14 rounded-md object-cover" />
            <p className="text-sm flex-1">Foto selezionata</p>
            {!showDesigner && (
              <Button size="sm" variant="outline" onClick={() => setShowDesigner(true)} data-testid="picker-designer-btn"><Palette className="w-3.5 h-3.5 mr-1" /> Personalizza</Button>
            )}
            <Button size="sm" onClick={() => save()} disabled={saving} data-testid="picker-save-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />} Usa così
            </Button>
          </div>
        )}

        {selected && showDesigner && (
          <DesignerPanel mediaUrl={selected} defaultHeadline={headlineGuess} defaultFormat={post?.post_format === 'story' ? 'story' : 'feed'} onApply={(url) => save(url)} onCancel={() => setShowDesigner(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PhotoPickerDialog;
